import ConfirmationDialog from './ConfirmationDialog.js';
import DestructiveRequest from './DestructiveRequest.js';

/**
 * TippyConfirm
 * ------------
 * Confirmation handler: TippyManager owns the hover tooltip, while the
 * shared ConfirmationDialog owns the one application-wide confirmation popup.
 *
 * Preferred markup:
 *
 * <button
 *   data-action="confirm"
 *   data-tooltip="page"
 *   data-tooltip-trigger="hover"
 *   data-tooltip-content="Delete"
 *   data-confirm-message="Delete this item?"
 *   data-url="/api/delete/123"
 * ></button>
 */
class TippyConfirm {
    constructor(selector = '[data-action="confirm"]', tippyManager, options = {}) {
        if (!tippyManager) {
            throw new Error('TippyConfirm: tippyManager is required');
        }

        this.selector = selector;
        this.tippyManager = tippyManager;

        if (!options.translator) {
            throw new Error('TippyConfirm: translator is required');
        }
        this.translator = options.translator;

        this.options = Object.assign({
            approveText: this.translator.trans('confirm.approve'),
            cancelText: this.translator.trans('confirm.cancel'),
            confirmMessage: this.translator.trans('confirm.message'),
            fetchOptions: {},
            urlAttribute: 'data-url',
            resultHtmlContainer: '#confirm-action-result-html',
            toastNotifier: null,
        }, options);

        this.toastNotifier = this.options.toastNotifier;
        this.confirmationDialog = options.confirmationDialog || new ConfirmationDialog((key) => this.translator.trans(key));
        this.destructiveRequest = options.destructiveRequest || new DestructiveRequest();

        if (!this.toastNotifier) {
            throw new Error('TippyConfirm: toastNotifier is required');
        }

        this.states = new WeakMap();
        this._ensureResultHtmlContainer();
    }

    /**
     * Initialize actions in root, including root itself.
     *
     * @param {ParentNode|Element} root
     */
    init(root = document) {
        this._collect(root).forEach((button) => this.attach(button));
    }

    /**
     * Attach confirm behavior without creating a second Tippy instance.
     *
     * @param {HTMLElement} button
     */
    attach(button) {
        if (button.dataset.confirmInitialized === 'true') {
            return;
        }

        const instance = this.tippyManager.getOrCreate(button);
        const state = {
            instance,
            busy: false,
            clickHandler: null,
        };

        state.clickHandler = (event) => {
            event.preventDefault();
            event.stopPropagation();
            this._showConfirmation(button, state);
        };

        button.addEventListener('click', state.clickHandler);

        this.states.set(button, state);
        button.dataset.confirmInitialized = 'true';
    }

    detach(button, {destroyTippy = true} = {}) {
        const state = this.states.get(button);

        if (!state) {
            return;
        }

        if (state.clickHandler) {
            button.removeEventListener('click', state.clickHandler);
        }

        if (destroyTippy) {
            this.tippyManager.destroy(button);
        } else {
            this.tippyManager.restore(button);
        }

        this.states.delete(button);
        delete button.dataset.confirmInitialized;
    }

    async _showConfirmation(button, state) {
        if (state.busy) return;
        state.busy = true;
        state.instance?.hide?.();
        try {
            const message = button.dataset.confirmMessage || this.options.confirmMessage;
            const restoration = /ripristin|riattiv|undelet|restore|retry|riprova|annullare la cancellazione/i.test(message);
            const danger = !restoration && (button.dataset.confirmDestructive === 'true' ||
                /elimin|cancell|delete|remove|rimuov|reset/i.test(message));
            const accepted = await this.confirmationDialog.ask(
                message,
                button,
                {destructive: danger, note: danger ? undefined : false,
                 approveLabel: button.dataset.approveText || (danger ? undefined : this.options.approveText),
                 cancelLabel: button.dataset.cancelText || this.options.cancelText}
            );
            if (!accepted) return;
            if (button.dataset.confirmSubmitForm) {
                const form = this._resolveSubmitForm(button);
                if (!form) {
                    this.toastNotifier.error(this.translator.trans('common.error'));
                    return;
                }
                // Preserve the existing regular POST and CSRF-protected form.
                form.submit();
                return;
            }
            const url = button.getAttribute(this.options.urlAttribute);
            if (!url) {
                this.toastNotifier.error(this.translator.trans('confirm.missingUrl'));
                return;
            }
            const response = await this.destructiveRequest.post(url, {as: 'json'});
            if (!response.ok) throw new Error(`HTTP ${response.status}`);
            this._handleResponse(await response.json(), button);
        } catch (error) {
            console.error('Confirmed action failed:', error);
            this.toastNotifier.error(
                this.translator.trans('confirm.networkError'),
                {title: this.translator.trans('confirm.connectionError')}
            );
        } finally {
            state.busy = false;
        }
    }

    _resolveSubmitForm(button) {
        const selector = button.dataset.confirmSubmitForm;
        return selector === 'closest' ? button.closest('form') : document.querySelector(selector);
    }

    _buildFetchOptions() {
        const custom = this.options.fetchOptions || {};

        return {
            ...custom,
            method: custom.method || 'POST',
            headers: {
                'X-Requested-With': 'XMLHttpRequest',
                'Content-Type': 'application/json',
                ...(custom.headers || {}),
            },
        };
    }

    _handleResponse(data, button) {
        if (data.js) {
            this._injectHtmlAndRunJs(data.js);
        }

        if (data.html) {
            this._updateTarget(button, data.html);
        }

        this._showFlashes(data.flashes);

        if (data.success && !data.flashes) {
            this.toastNotifier.success(
                this.translator.trans('confirm.completed'),
                {title: this.translator.trans('common.success')}
            );
        }

        if (data.success || data.removeAnyway) {
            this._removeTargetElement(button);
        }

        if (!data.success && data.message) {
            this.toastNotifier.error(data.message, {title: this.translator.trans('common.error')});
        }
    }

    _updateTarget(button, html) {
        const selector = button.dataset.updateTarget;

        if (!selector) {
            return;
        }

        const target = document.querySelector(selector);

        if (target) {
            target.innerHTML = html;
        }
    }

    _showFlashes(flashes) {
        if (!flashes) {
            return;
        }

        Object.entries(flashes).forEach(([type, messages]) => {
            messages.forEach((message) => {
                this.toastNotifier.show({
                    type,
                    title: this._getFlashTitle(type),
                    message,
                });
            });
        });
    }

    _getFlashTitle(type) {
        switch (type) {
            case 'success':
                return this.translator.trans('common.success');
            case 'danger':
            case 'error':
                return this.translator.trans('common.error');
            case 'warning':
                return this.translator.trans('common.warning');
            default:
                return this.translator.trans('common.info');
        }
    }

    _removeTargetElement(button) {
        const selector = button.dataset.removeTarget;

        if (!selector) {
            return;
        }

        const element = document.querySelector(selector);

        if (!element) {
            return;
        }

        element.classList.add('confirm-remove-transition');

        let removed = false;
        const remove = () => {
            if (removed) {
                return;
            }
            removed = true;
            element.remove();
        };

        element.addEventListener('transitionend', remove, {once: true});
        setTimeout(remove, 350);
    }

    _injectHtmlAndRunJs(jsAndHtml) {
        const container = document.createElement('div');
        container.innerHTML = jsAndHtml;

        const scripts = [...container.querySelectorAll('script')];
        scripts.forEach((script) => script.remove());

        const target = this._getResultHtmlContainer();

        if (!target) {
            return;
        }

        target.innerHTML = container.innerHTML;

        scripts.forEach((script) => {
            const newScript = document.createElement('script');

            if (script.src) {
                newScript.src = script.src;
            } else {
                newScript.textContent = script.textContent;
            }

            target.appendChild(newScript);
        });
    }

    _getResultHtmlContainer() {
        let target = document.querySelector(this.options.resultHtmlContainer);

        if (!target) {
            this._ensureResultHtmlContainer();
            target = document.querySelector(this.options.resultHtmlContainer);
        }

        return target;
    }

    _ensureResultHtmlContainer() {
        if (document.querySelector(this.options.resultHtmlContainer)) {
            return;
        }

        if (!this.options.resultHtmlContainer.startsWith('#')) {
            return;
        }

        const container = document.createElement('div');
        container.id = this.options.resultHtmlContainer.slice(1);
        document.body.appendChild(container);
    }

    _collect(root) {
        const elements = [];

        if (!root) {
            return elements;
        }

        if (typeof root.matches === 'function' && root.matches(this.selector)) {
            elements.push(root);
        }

        if (typeof root.querySelectorAll === 'function') {
            elements.push(...root.querySelectorAll(this.selector));
        }

        return elements;
    }


}

export default TippyConfirm;
